import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import AdvanceTableResponsive from '../../../components/common/advance-table/AdvanceTableResponsive';
import CardDropdown from '../../../components/common/CardDropdown';
import PendingListLoadingState from './common/PendingListLoadingState';
import useAdvanceTable from '../../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../../providers/AdvanceTableProvider';
import { useOrderContext } from '../../../providers/OrderProvider';
import { socket } from '../../../socket';
import formatCurrency from '../../../utils/formatCurrency';
import { getOrderPendingPrepaymentAmount, TAIL_QUASI_RECEIVABLE_LABEL } from '../../../utils/order/orderAccountsReceivable';
import PendingListStatusBadge from './common/PendingListStatusBadge';
import PendingListEmptyState from './common/PendingListEmptyState';
import { PendingKeywordHighlight, PendingTotalPriceHighlight, PendingAmountHighlight } from './common/PendingKeywordHighlight';
import PendingListTableFooter from './common/PendingListTableFooter';
import PendingListToolbar from './common/PendingListToolbar';
import PendingListStatsPanel from './common/PendingListStatsPanel';
import PendingListStatsOverlay from './common/PendingListStatsOverlay';
import PendingOrderIdCell from './common/PendingOrderIdCell';
import { getPendingListStatusBadgeProps } from './common/pendingRowStatusDisplay';
import { PENDING_TAB_BADGE, PENDING_TABLE_PER_PAGE } from './common/pendingTableConfig';
import { buildPendingActivityAtColumn, PENDING_ACTIVITY_AT_SORT } from './common/pendingActivityAtColumn';
import { usePendingKeywordFilter } from './common/usePendingKeywordFilter';
import PrePaymentModal from './modal/PrePaymentModal';

const PrePaymentOrderPendings = () => {
	const [isOpenPendingModal, setIsOpenPendingModal] = useState(false);
	const {
		prePaymentOrders = [],
		prePaymentTotalPriceSum = 0,
		prePaymentTotalPrePaymentAmount = 0,
		prePaymentTotalQuasiReceivable = 0,
		isLoadingPrePaymentOrder,
		isFetchingPrePaymentOrder,
		isPrePaymentOrdersUninitialized,
		setCurrentOrderOIdState,
	} = useOrderContext();

	const [prePaymentPendingStat, setPrePaymentPendingStat] = useState('total'); // total | prePaymentAmount | quasiReceivable

	useEffect(() => {
		if (prePaymentPendingStat === 'quasiReceivable' && Number(prePaymentTotalQuasiReceivable) <= 0) {
			setPrePaymentPendingStat('total');
		}
	}, [prePaymentPendingStat, prePaymentTotalQuasiReceivable]);

	const getOrderPrePaymentAmount = useCallback((order) => getOrderPendingPrepaymentAmount(order), []);

	const handleOrderClick = useCallback(
		(orderId) => {
			setIsOpenPendingModal(true);
			setCurrentOrderOIdState(orderId);
			socket.emit('join order', orderId);
		},
		[setCurrentOrderOIdState],
	);

	const formatDateTime = useCallback((date) => {
		return moment(date).format('YYYY-MM-DD HH:mm');
	}, []);

	const handlePrePaymentStatClick = useCallback((statKey) => {
		setPrePaymentPendingStat(statKey);
	}, []);

	const statsItems = useMemo(() => {
		const source = Array.isArray(prePaymentOrders) ? prePaymentOrders : [];
		const items = [];
		if (Number(prePaymentTotalPriceSum) > 0) {
			items.push({
				key: 'total',
				label: '总计',
				amount: Number(prePaymentTotalPriceSum),
				count: source.length,
				active: prePaymentPendingStat === 'total',
			});
		}
		if (Number(prePaymentTotalPrePaymentAmount) > 0) {
			items.push({
				key: 'prePaymentAmount',
				label: '待预付总额',
				amount: Number(prePaymentTotalPrePaymentAmount),
				count: source.filter((order) => getOrderPrePaymentAmount(order) > 0).length,
				active: prePaymentPendingStat === 'prePaymentAmount',
			});
		}
		if (Number(prePaymentTotalQuasiReceivable) > 0) {
			items.push({
				key: 'quasiReceivable',
				label: TAIL_QUASI_RECEIVABLE_LABEL,
				amount: Number(prePaymentTotalQuasiReceivable),
				count: source.filter((order) => Number(order?.quasiReceivable) > 0).length,
				active: prePaymentPendingStat === 'quasiReceivable',
			});
		}
		return items;
	}, [
		prePaymentOrders,
		prePaymentTotalPriceSum,
		prePaymentTotalPrePaymentAmount,
		prePaymentTotalQuasiReceivable,
		prePaymentPendingStat,
		getOrderPrePaymentAmount,
	]);

	const columns = useMemo(() => {
		const amountColumns = [];

		if (prePaymentPendingStat === 'prePaymentAmount') {
			amountColumns.push({
				id: 'prePaymentAmount',
				accessorFn: (row) => getOrderPrePaymentAmount(row),
				header: '预付金额',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-end' },
					cellProps: { className: 'text-end' },
				},
				cell: ({ row: { original } }) => {
					const amount = getOrderPrePaymentAmount(original);
					return (
						<PendingListStatusBadge
							bg={PENDING_TAB_BADGE.prePayment}
							text={formatCurrency(amount)}
							pill
							showIcon={false}
						/>
					);
				},
			});
		}

		if (prePaymentPendingStat === 'quasiReceivable') {
			amountColumns.push({
				accessorKey: 'quasiReceivable',
				header: TAIL_QUASI_RECEIVABLE_LABEL,
				meta: {
					headerProps: { className: 'pe-3 text-900 text-end' },
					cellProps: { className: 'text-end' },
				},
				cell: ({ row: { original } }) => {
					const quasiAmount = Number(original.quasiReceivable) || 0;
					return (
						<PendingListStatusBadge
							bg='success'
							text={<PendingAmountHighlight amount={quasiAmount} />}
							pill
							showIcon={false}
						/>
					);
				},
			});
		}

		return [
			{
				accessorKey: 'orderId',
				header: '订单编号',
				meta: {
					headerProps: { className: 'sticky-column text-900 text-start' },
					cellProps: {
						className: 'fw-semibold text-start',
					},
				},
				cell: ({ row: { original } }) => {
					const { orderId, _id } = original;
					return (
						<PendingOrderIdCell
							orderId={orderId}
							orderMongoId={_id}
							badgeVariant={PENDING_TAB_BADGE.prePayment}
							onOrderClick={handleOrderClick}
							order={original}
						/>
					);
				},
			},
			{
				accessorKey: 'customerInfo.customerName',
				header: '客户名称',
				meta: {
					headerProps: { className: 'pe-3 text-900 text-start' },
					cellProps: { className: 'text-start' },
				},
				cell: ({ row: { original } }) => {
					const { customerInfo } = original;
					return (
						<span className={`link-${PENDING_TAB_BADGE.prePayment}`}>
							<PendingKeywordHighlight text={customerInfo?.customerName} />
						</span>
					);
				},
			},
			...amountColumns,
			{
				accessorKey: 'paymentStatus',
				header: '支付状态',
				meta: {
					headerProps: { className: 'pe-3 text-900' },
				},
				cell: ({ row: { original } }) => {
					const config = getPendingListStatusBadgeProps(
						original.paymentStatus,
						PENDING_TAB_BADGE.prePayment,
						'payment',
					);
					return config ? <PendingListStatusBadge bg={config.bg} text={config.text} /> : '-';
				},
			},
			{
				accessorKey: 'totalPrice',
				header: '总价',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <PendingTotalPriceHighlight totalPrice={original?.totalPrice} className='fs-11' />;
				},
			},
			buildPendingActivityAtColumn(formatDateTime),
			{
				accessorKey: 'createdAt',
				header: '创建时间',
				meta: {
					headerProps: {
						className: 'text-end text-900',
					},
					cellProps: {
						className: 'text-end',
					},
				},
				cell: ({ row: { original } }) => {
					return <span className='fs-11'>{original?.createdAt ? formatDateTime(original.createdAt) : '-'}</span>;
				},
			},
			{
				accessorKey: 'actions',
				header: '',
				enableSorting: false,
				meta: {
					cellProps: {
						className: 'text-end py-2',
					},
				},
				cell: ({ row: { original } }) => {
					const detailUrl = original?._id ? `/order/detail/${original._id}` : '#';
					return (
						<CardDropdown drop='start'>
							<div className='py-2'>
								<Dropdown.Item href={detailUrl}>查看</Dropdown.Item>
							</div>
						</CardDropdown>
					);
				},
			},
		];
	}, [prePaymentPendingStat, handleOrderClick, formatDateTime, getOrderPrePaymentAmount]);

	const displayedPrePaymentOrders = useMemo(() => {
		if (!Array.isArray(prePaymentOrders) || prePaymentOrders.length === 0) return [];
		if (prePaymentPendingStat === 'total') return prePaymentOrders;

		return prePaymentOrders.filter((order) => {
			if (prePaymentPendingStat === 'prePaymentAmount') {
				return getOrderPrePaymentAmount(order) > 0;
			}
			if (prePaymentPendingStat === 'quasiReceivable') {
				return Number(order?.quasiReceivable) > 0;
			}
			return true;
		});
	}, [prePaymentOrders, prePaymentPendingStat, getOrderPrePaymentAmount]);

	const { keyword, hasKeyword, filteredOrders } = usePendingKeywordFilter(displayedPrePaymentOrders);

	const table = useAdvanceTable({
		data: filteredOrders,
		columns,
		sortable: true,
		pagination: true,
		perPage: PENDING_TABLE_PER_PAGE,
		initialState: {
			sorting: PENDING_ACTIVITY_AT_SORT,
		},
	});

	useEffect(() => {
		try {
			table.setPageIndex(0);
		} catch {
			// ignore
		}
	}, [prePaymentPendingStat, table]);

	if (isPrePaymentOrdersUninitialized || isLoadingPrePaymentOrder || (isFetchingPrePaymentOrder && prePaymentOrders.length === 0)) {
		return <PendingListLoadingState />;
	}

	const showFooter = displayedPrePaymentOrders.length > 0 || hasKeyword;
	const emptyTitle = hasKeyword
		? '无匹配订单'
		: prePaymentPendingStat === 'prePaymentAmount'
			? '暂无待预付金额'
			: prePaymentPendingStat === 'quasiReceivable'
				? `暂无${TAIL_QUASI_RECEIVABLE_LABEL}`
				: '暂无预付款确认';
	const emptyDescription = hasKeyword ? `当前待办中没有包含「${keyword}」的订单。` : '目前没有需要你处理的预付款。';

	return (
		<>
			<AdvanceTableProvider {...table}>
				<PendingListToolbar
					statsMobileOnly
					stats={<PendingListStatsPanel items={statsItems} onSelect={handlePrePaymentStatClick} showChart={false} />}
					statsOverlay={<PendingListStatsOverlay items={statsItems} onSelect={handlePrePaymentStatClick} />}
				>
					{filteredOrders.length === 0 ? (
						<PendingListEmptyState emptyTitle={emptyTitle} emptyDescription={emptyDescription} />
					) : (
						<AdvanceTableResponsive
							headerClassName='bg-200 text-nowrap align-middle font-sans-serif'
							rowClassName='btn-reveal-trigger text-nowrap align-middle'
							tableProps={{
								className: 'fs-10 fw-semibold mb-0 overflow-hidden',
							}}
						/>
					)}
					{showFooter ? <PendingListTableFooter showTableControls={filteredOrders.length > 0} /> : null}
				</PendingListToolbar>
			</AdvanceTableProvider>
			<PrePaymentModal setIsOpenPendingModal={setIsOpenPendingModal} isOpenPendingModal={isOpenPendingModal} />
		</>
	);
};

export default PrePaymentOrderPendings;
